import { useCallback, useMemo, useState } from 'react';
import { ResponsiveGridProps } from '@cbhq/cds-web';
import { Button, IconButton } from '@cbhq/cds-web/buttons';
import { SelectOption } from '@cbhq/cds-web/controls';
import { Dropdown } from '@cbhq/cds-web/dropdown';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Box, Divider, Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import { Tooltip } from '@cbhq/cds-web/overlays';
import { TextBody, TextDisplay3, TextHeadline, TextTitle3 } from '@cbhq/cds-web/typography';

import { DashboardProvider } from ':cloud/contexts/DashboardProvider';
import { useProject } from ':cloud/contexts/ProjectContext';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { useGetStakingRewards } from ':cloud/queries/EarningsQueries/useGetStakingRewards';
import { Chart } from ':cloud/widgets/earnings/Chart';
import { RANGE } from ':cloud/widgets/earnings/config';
import { EarningsReport } from ':cloud/widgets/earnings/EarningsReport';
import { EarningsRange, ProtocolData } from ':cloud/widgets/earnings/types';
import { getLatestBalance, sumEarnings } from ':cloud/widgets/earnings/utils';
import { Layout } from ':cloud/widgets/layout';
import { CloudErrorBoundary } from ':cloud/widgets/sharedcomponents';

const responsiveGridConfig: ResponsiveGridProps = {
  phone: {
    columns: 1,
  },
  tablet: {
    columns: 1,
  },
  desktop: {
    columns: 1,
  },
};

const content = Object.entries(RANGE).map(([key, title]) => (
  <SelectOption borderRadius="rounded" key={key} value={key} title={title} />
));

const DEFAULT_PROTOCOL = 'ethereum';

function EarningsContent() {
  const { selectedProject } = useProject();
  const { isPhone } = useSimpleBreakpoints();
  const [range, setRange] = useState<EarningsRange>('currentMonth');
  const handleSwitchChartRange = useCallback(
    (value: EarningsRange) => {
      setRange(value);
    },
    [setRange],
  );
  const { isLoading, data } = useGetStakingRewards(
    { filter: range },
    selectedProject?.organizationId || '',
  );

  const filteredProtocolData: ProtocolData = useMemo(() => {
    const protocolReward = data.protocolRewards?.find((pr) => pr.protocol === DEFAULT_PROTOCOL);
    return {
      ticker: protocolReward?.ticker || '',
      rewards: protocolReward?.rewards || {},
      balances: protocolReward?.balances || {},
    };
  }, [data.protocolRewards]);

  const totalRewards = useMemo(() => {
    return sumEarnings(filteredProtocolData.rewards);
  }, [filteredProtocolData.rewards]);

  const latestBalance = useMemo(() => {
    return getLatestBalance(filteredProtocolData.balances);
  }, [filteredProtocolData.balances]);

  return (
    <VStack gap={10}>
      <VStack gap={3}>
        <HStack justifyContent="space-between" alignItems="center">
          <VStack justifyContent="space-between" spacingStart={1}>
            <TextTitle3 as="h1" spacingBottom={1}>
              View your staking rewards
            </TextTitle3>
            <TextBody as="p" color="foregroundMuted" spacingBottom={1}>
              View details on your staking performance and download monthly earning reports.
            </TextBody>
          </VStack>
          <HStack alignItems="center" spacingTop={3}>
            <Dropdown
              value={range}
              onChange={(value) => handleSwitchChartRange(value)}
              content={<VStack width={208}>{content}</VStack>}
              enableMobileModal
            >
              {isPhone ? (
                <IconButton name="more" />
              ) : (
                <Button compact endIcon="caretDown" variant="secondary">
                  {RANGE[range]}
                </Button>
              )}
            </Dropdown>
          </HStack>
        </HStack>

        <HStack justifyContent="flex-start" alignItems="center" gap={4}>
          <EarningsDetails
            title="Total Staked Balance"
            value={latestBalance}
            unit={filteredProtocolData.ticker}
            toolTipContent="The current value of assets actively locked in a staking position."
          />
          <EarningsDetails
            title="Total Gross Rewards"
            value={totalRewards.totalGrossEarnedNativeUnits}
            unit={filteredProtocolData.ticker}
            toolTipContent="The total number of rewards earned to date before operator commission fees are taken."
          />
          <EarningsDetails
            title="Total Net Rewards"
            value={totalRewards.totalEarnedNativeUnits}
            unit={filteredProtocolData.ticker}
            toolTipContent="The total number of rewards distributed to the beneficiary address to date once commission fees are deducted."
          />
          <EarningsDetails
            title="Commission Fee"
            value={totalRewards.totalCommissionNativeUnits}
            unit={filteredProtocolData.ticker}
            toolTipContent="The total amount of commission fees deducted from gross rewards to date. Commission fees may also include any 3rd party fees."
          />
        </HStack>

        <Grid responsiveConfig={responsiveGridConfig} gap={5}>
          <Chart
            key="EarningsChart"
            chartType="area"
            isLoading={isLoading}
            protocolData={filteredProtocolData}
            latestStakeBalance={latestBalance}
            totalGrossRewards={totalRewards.totalGrossEarnedNativeUnits}
            totalNetRewards={totalRewards.totalEarnedNativeUnits}
          />
        </Grid>
      </VStack>
    </VStack>
  );
}

interface EarningsDetailsProps {
  title: string;
  value: string;
  unit: string;
  toolTipContent: string;
}

function EarningsDetails({ title, value, unit, toolTipContent }: EarningsDetailsProps) {
  let text = value;
  if (value !== '--') {
    text += ` ${unit}`;
  }

  return (
    <Box bordered borderRadius="roundedSmall" display="flex" spacing={2} width="25%">
      <VStack spacing={2}>
        <HStack gap={0.5}>
          <TextHeadline as="p" color="foregroundMuted">
            {title}
          </TextHeadline>
          <Tooltip content={toolTipContent}>
            <Icon name="info" size="s" color="foregroundMuted" spacingTop={0.5} />
          </Tooltip>
        </HStack>
        <TextDisplay3 as="h2">{text}</TextDisplay3>
      </VStack>
    </Box>
  );
}

export function EarningsPage() {
  return (
    <>
      <Layout>
        <Layout.MainContainer fullWidth>
          <CloudErrorBoundary name="EarningsContent">
            <DashboardProvider>
              <EarningsContent />
            </DashboardProvider>
          </CloudErrorBoundary>
        </Layout.MainContainer>
      </Layout>
      <Divider spacingVertical={2} />
      <Layout>
        <Layout.MainContainer fullWidth>
          <CloudErrorBoundary name="EarningsReportContent">
            <EarningsReport />
          </CloudErrorBoundary>
        </Layout.MainContainer>
      </Layout>
    </>
  );
}
